import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Input, Switch, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";

import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import AppContext from "../../../../context/AppContext";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";

import "./kycCheckpoints.css";

const FLOWS = ["onboarding", "sign_in", "deposit", "withdrawal"] as const;
type Flow = (typeof FLOWS)[number];

interface SurtCheckpoint {
  checkpoint_id: string;
  enabled: boolean;
}

interface SurtCheckpointsSnapshot {
  flows: Record<Flow, SurtCheckpoint>;
}

type KycCheckpointsAppContext = {
  adminPermissions?: string[];
};

const FLOW_LABELS: Record<Flow, string> = {
  onboarding: AppStrings.KYC_CHECKPOINTS_ONBOARDING,
  sign_in: AppStrings.KYC_CHECKPOINTS_SIGN_IN,
  deposit: AppStrings.KYC_CHECKPOINTS_DEPOSIT,
  withdrawal: AppStrings.KYC_CHECKPOINTS_WITHDRAWAL,
};

const EDIT_PERMISSION = "compliance.configuration.kyc_checkpoints";

function emptyInputs(): Record<Flow, string> {
  return { onboarding: "", sign_in: "", deposit: "", withdrawal: "" };
}

function emptyErrors(): Record<Flow, string | null> {
  return { onboarding: null, sign_in: null, deposit: null, withdrawal: null };
}

function inputsFromSnapshot(snap: SurtCheckpointsSnapshot): Record<Flow, string> {
  return {
    onboarding: snap.flows.onboarding.checkpoint_id ?? "",
    sign_in: snap.flows.sign_in.checkpoint_id ?? "",
    deposit: snap.flows.deposit.checkpoint_id ?? "",
    withdrawal: snap.flows.withdrawal.checkpoint_id ?? "",
  };
}

function isCheckpointEntry(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function extractSnapshot(raw: unknown): SurtCheckpointsSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const candidates: Record<string, unknown>[] = [raw as Record<string, unknown>];
  const r = raw as Record<string, unknown>;
  if (r.data && typeof r.data === "object") {
    candidates.push(r.data as Record<string, unknown>);
  }
  for (const candidate of candidates) {
    if (FLOWS.every((f) => isCheckpointEntry(candidate[f]))) {
      const flows = {} as Record<Flow, SurtCheckpoint>;
      for (const f of FLOWS) {
        const entry = candidate[f] as Record<string, unknown>;
        flows[f] = {
          checkpoint_id:
            typeof entry.checkpoint_id === "string" ? entry.checkpoint_id : "",
          enabled: Boolean(entry.enabled),
        };
      }
      return { flows };
    }
  }
  return null;
}

interface TableRow {
  key: Flow;
  flow: Flow;
}

const KycCheckpoints: React.FC = () => {
  const { adminPermissions } = useContext(
    AppContext,
  ) as KycCheckpointsAppContext;
  const token = JSON.parse(localStorage.getItem("token") || "null");
  const client = useMemo(
    () => createPrivateAdminPanelInstance(token?.AccessToken),
    [token?.AccessToken],
  );

  // Two-level gate: SURT is the provider switch, surtCheckpoints is the
  // checkpoints master switch on top of it. Both have to be on before the
  // table means anything.
  const { isEnabled: surtEnabled, loading: surtLoading } = usePamFeature(
    "surt",
  ) as { isEnabled: boolean | null; loading: boolean; error: Error | null };
  const { isEnabled: checkpointsEnabled, loading: checkpointsLoading } =
    usePamFeature("surtCheckpoints") as {
      isEnabled: boolean | null;
      loading: boolean;
      error: Error | null;
    };

  const flagsLoading = surtLoading || checkpointsLoading;
  const flagsEnabled = Boolean(surtEnabled && checkpointsEnabled);

  const canEdit = useMemo(() => {
    const p = adminPermissions ?? [];
    return p.includes(EDIT_PERMISSION);
  }, [adminPermissions]);

  const [snapshot, setSnapshot] = useState<SurtCheckpointsSnapshot | null>(null);
  const [inputs, setInputs] = useState<Record<Flow, string>>(emptyInputs);
  const [rowErrors, setRowErrors] =
    useState<Record<Flow, string | null>>(emptyErrors);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<Flow | null>(null);

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await client.get(API_ROUTES.GET_SURT_CHECKPOINTS);
      const snap = extractSnapshot(response?.data);
      if (!snap) {
        setLoadError(AppStrings.KYC_CHECKPOINTS_LOAD_ERROR);
        setSnapshot(null);
        setInputs(emptyInputs());
      } else {
        setSnapshot(snap);
        setInputs(inputsFromSnapshot(snap));
      }
      setRowErrors(emptyErrors());
    } catch (err) {
      const msg = getApiErrorMessage(
        err,
        AppStrings.KYC_CHECKPOINTS_LOAD_ERROR,
      );
      setLoadError(msg);
      setSnapshot(null);
      setInputs(emptyInputs());
      setRowErrors(emptyErrors());
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    // No SURT or no checkpoints feature: skip the round-trip entirely.
    if (flagsLoading || !flagsEnabled) return;
    fetchSnapshot();
  }, [fetchSnapshot, flagsEnabled, flagsLoading]);

  const upsertRow = useCallback(
    async (flow: Flow, next: SurtCheckpoint) => {
      if (!snapshot) return;
      const prev = snapshot.flows[flow];
      setSavingKey(flow);
      setSnapshot((cur) =>
        cur ? { ...cur, flows: { ...cur.flows, [flow]: next } } : cur,
      );
      setInputs((s) => ({ ...s, [flow]: next.checkpoint_id }));
      setRowErrors((e) => ({ ...e, [flow]: null }));
      try {
        await client.put(API_ROUTES.UPDATE_SURT_CHECKPOINT, {
          flow,
          checkpoint_id: next.checkpoint_id,
          enabled: next.enabled,
        });
        message.success(AppStrings.KYC_CHECKPOINTS_SAVE_SUCCESS);
      } catch (err) {
        setSnapshot((cur) =>
          cur ? { ...cur, flows: { ...cur.flows, [flow]: prev } } : cur,
        );
        setInputs((s) => ({ ...s, [flow]: prev.checkpoint_id }));
        // The PUT is still gated by the master feature flag: a 403 means the
        // feature is off server-side. Revert the row and explain why the save
        // did not stick.
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 403) {
          message.error(AppStrings.KYC_CHECKPOINTS_FEATURE_DISABLED);
        } else {
          message.error(
            getApiErrorMessage(err, AppStrings.KYC_CHECKPOINTS_SAVE_ERROR),
          );
        }
      } finally {
        setSavingKey(null);
      }
    },
    [client, snapshot],
  );

  const handleInputChange = useCallback((flow: Flow, value: string) => {
    setInputs((s) => ({ ...s, [flow]: value }));
    // Typing is the fix for an empty-id error, so clear it as soon as the
    // field is no longer blank.
    if (value.trim() !== "") {
      setRowErrors((e) => (e[flow] ? { ...e, [flow]: null } : e));
    }
  }, []);

  const handleInputBlur = useCallback(
    (flow: Flow) => {
      if (!snapshot || !canEdit || savingKey === flow) return;
      const serverEntry = snapshot.flows[flow];
      const trimmed = inputs[flow].trim();
      if (trimmed === serverEntry.checkpoint_id) {
        if (inputs[flow] !== trimmed) {
          setInputs((s) => ({ ...s, [flow]: trimmed }));
        }
        return;
      }
      // Editing the id is treated as (re)provisioning the checkpoint: any
      // non-empty id auto-enables the row, and clearing it disables it. To
      // keep a row disabled, operators toggle the Switch after the id is set.
      const nextEnable = trimmed !== "";
      upsertRow(flow, { checkpoint_id: trimmed, enabled: nextEnable });
    },
    [canEdit, inputs, savingKey, snapshot, upsertRow],
  );

  const handleSwitchChange = useCallback(
    (flow: Flow, nextEnable: boolean) => {
      if (!snapshot || !canEdit || savingKey === flow) return;
      const trimmed = inputs[flow].trim();
      // Enabling a flow without an id is a user error, not a no-op: say so.
      if (nextEnable && trimmed === "") {
        setRowErrors((e) => ({
          ...e,
          [flow]: AppStrings.KYC_CHECKPOINTS_ID_REQUIRED,
        }));
        return;
      }
      upsertRow(flow, { checkpoint_id: trimmed, enabled: nextEnable });
    },
    [canEdit, inputs, savingKey, snapshot, upsertRow],
  );

  const columns: ColumnsType<TableRow> = useMemo(
    () => [
      {
        title: AppStrings.KYC_CHECKPOINTS_FLOW,
        dataIndex: "flow",
        key: "flow",
        width: 200,
        render: (flow: Flow) => FLOW_LABELS[flow],
      },
      {
        title: AppStrings.KYC_CHECKPOINTS_CHECKPOINT_ID,
        key: "checkpoint_id",
        render: (_: unknown, record: TableRow) => {
          const flow = record.flow;
          const value = inputs[flow] ?? "";
          const error = rowErrors[flow];
          const disabled = !snapshot || !canEdit || savingKey === flow;
          return (
            <>
              <Input
                aria-label={`${FLOW_LABELS[flow]} ${AppStrings.KYC_CHECKPOINTS_CHECKPOINT_ID}`}
                className="kyc-checkpoints-input"
                value={value}
                disabled={disabled}
                status={error ? "error" : undefined}
                onChange={(e) => handleInputChange(flow, e.target.value)}
                onBlur={() => handleInputBlur(flow)}
              />
              {error ? (
                <div className="kyc-checkpoints-field-error" role="alert">
                  {error}
                </div>
              ) : null}
            </>
          );
        },
      },
      {
        title: AppStrings.KYC_CHECKPOINTS_ENABLED,
        key: "enable",
        width: 120,
        align: "center" as const,
        render: (_: unknown, record: TableRow) => {
          const flow = record.flow;
          const checked = snapshot ? snapshot.flows[flow].enabled : false;
          // Deliberately still attemptable with an empty id — the click is how
          // the operator surfaces the ID_REQUIRED validation error.
          const disabled = !snapshot || !canEdit || savingKey === flow;
          return (
            <Switch
              className="kyc-checkpoints-switch"
              aria-label={`${FLOW_LABELS[flow]} ${AppStrings.KYC_CHECKPOINTS_ENABLED}`}
              checked={checked}
              disabled={disabled}
              onChange={(next) => handleSwitchChange(flow, next)}
            />
          );
        },
      },
    ],
    [
      canEdit,
      handleInputBlur,
      handleInputChange,
      handleSwitchChange,
      inputs,
      rowErrors,
      savingKey,
      snapshot,
    ],
  );

  const dataSource: TableRow[] = useMemo(
    () => FLOWS.map((f) => ({ key: f, flow: f })),
    [],
  );

  if (flagsLoading || (loading && !snapshot && !loadError && flagsEnabled)) {
    return (
      <div className="kyc-checkpoints-loading">
        <RebetLoader height="32px" width="32px" />
      </div>
    );
  }

  if (!flagsEnabled) {
    return (
      <div className="kyc-checkpoints-panel">
        <div className="kyc-checkpoints-notice" role="status">
          {surtEnabled
            ? AppStrings.KYC_CHECKPOINTS_FEATURE_DISABLED_NOTICE
            : AppStrings.KYC_CHECKPOINTS_SURT_DISABLED}
        </div>
      </div>
    );
  }

  return (
    <div className="kyc-checkpoints-panel">
      <h2 className="kyc-checkpoints-panel-title">
        {AppStrings.KYC_CHECKPOINTS}
      </h2>
      {loadError ? (
        <div className="kyc-checkpoints-error" role="alert">
          <span>{loadError}</span>
          <Button
            className="kyc-checkpoints-retry"
            onClick={() => fetchSnapshot()}
            disabled={loading}
          >
            {AppStrings.KYC_CHECKPOINTS_RETRY}
          </Button>
        </div>
      ) : null}
      <Table<TableRow>
        size="small"
        pagination={false}
        rowKey="key"
        columns={columns}
        dataSource={dataSource}
        className="kyc-checkpoints-table"
      />
    </div>
  );
};

export default KycCheckpoints;
